// hooks/useSessionSaving.js - VERSION PRESTATIONS
import { useCallback } from 'react';
import logger from '../utils/logger';
import { API_ENDPOINTS } from '../utils/apiConfig';
import { useTracking } from './useTracking';
import { TRACKING_EVENTS } from '../utils/trackingEvents';

/**
 * Hook pour la sauvegarde des prestations
 *
 * NOTE : Les heures sont gérées via les feuilles journalières (DailyWorksheet)
 * Ce hook gère uniquement :
 * - La création/modification de la prestation
 * - L'affectation des participants par jour
 * - Le flag "sur place" (is_on_site) par participant
 */
export const useSessionSaving = ({
  token,
  editingSession,
  formData,
  multiDayInfo,
  conflicts,
  showSuccess,
  showError,
  showConfirmation,
  onSave,
  onClose
}) => {
  // Alias pour compatibilite
  const editingPrestation = editingSession;

  // Hook de tracking
  const { trackAction } = useTracking();

  // Fonction de conversion date UTC
  const convertFormDateToUTC = useCallback((dateString) => {
    if (!dateString) return '';

    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString + 'T12:00:00.000Z';
    }

    return dateString;
  }, []);

  // Fonction de nettoyage des donnees
  const cleanFormDataForServer = useCallback((formData) => {
    const cleaned = { ...formData };

    // Convertir les strings vides en null pour les UUID
    const uuidFields = ['group_id', 'room_id', 'team_leader_id', 'trainer_id', 'status_id', 'client_id', 'location_id'];
    uuidFields.forEach(field => {
      if (cleaned[field] === '' || cleaned[field] === undefined) {
        cleaned[field] = null;
      }
    });

    // Convertir les strings vides en null pour les champs texte optionnels
    const optionalTextFields = ['departure_time', 'end_time', 'block_reason', 'maintenance_description', 'order_number', 'work_type', 'objective'];
    optionalTextFields.forEach(field => {
      if (cleaned[field] === '') {
        cleaned[field] = null;
      }
    });

    return cleaned;
  }, []);

  // Sauvegarde multi-jours
  const saveMultiDaySession = useCallback(async (sessionDays, dayParticipants, participantOnSite = {}) => {
    logger.debug('=== SAUVEGARDE MULTI-JOURS PRESTATIONS ===');
    logger.debug('Parametres recus:');
    logger.debug('   - sessionDays:', sessionDays);
    logger.debug('   - dayParticipants:', dayParticipants);
    logger.debug('   - participantOnSite:', participantOnSite);

    try {
      const cleanedFormData = cleanFormDataForServer(formData);
      let prestationId = editingPrestation?.id;
      const isCreating = !prestationId;

      logger.debug('Mode de sauvegarde:', isCreating ? 'CREATION' : 'MODIFICATION');

      // Protection
      if (!multiDayInfo?.days || multiDayInfo.days.length === 0) {
        throw new Error('Aucun jour defini pour la prestation');
      }

      // ETAPE 1: PRESTATION PRINCIPALE
      logger.debug('=== 1. SAUVEGARDE PRESTATION PRINCIPALE ===');

      const prestationUrl = prestationId
        ? API_ENDPOINTS.PRESTATION_BY_ID(prestationId)
        : API_ENDPOINTS.PRESTATIONS;
      const prestationMethod = prestationId ? 'PUT' : 'POST';

      const prestationType = cleanedFormData.prestation_type || cleanedFormData.session_type;

      // Pour blocage/note : extraire les participants de dayParticipants
      let extractedParticipantIds = [];
      if ((prestationType === 'blocked' || prestationType === 'note') && dayParticipants) {
        const allParticipants = new Set();
        Object.values(dayParticipants).forEach(dayList => {
          if (Array.isArray(dayList)) {
            dayList.forEach(id => allParticipants.add(id));
          }
        });
        extractedParticipantIds = Array.from(allParticipants);
        logger.debug(`Participants extraits pour ${prestationType}:`, extractedParticipantIds);
      }

      const prestationData = {
        prestation_type: prestationType,
        group_id: cleanedFormData.group_id || cleanedFormData.room_id,
        client_id: cleanedFormData.client_id,
        location_id: cleanedFormData.location_id,
        team_leader_id: cleanedFormData.team_leader_id || cleanedFormData.trainer_id,
        status_id: cleanedFormData.status_id,
        start_date: convertFormDateToUTC(cleanedFormData.start_date),
        end_date: convertFormDateToUTC(cleanedFormData.end_date),
        departure_time: cleanedFormData.departure_time,
        end_time: cleanedFormData.end_time,
        needs_lunch: cleanedFormData.needs_lunch,
        needs_bag: cleanedFormData.needs_bag,
        accommodation_type: cleanedFormData.accommodation_type,
        order_number: cleanedFormData.order_number,
        work_type: cleanedFormData.work_type,
        objective: cleanedFormData.objective,
        block_reason: cleanedFormData.block_reason,
        maintenance_description: cleanedFormData.maintenance_description,
        // Utiliser les participants extraits pour blocages/notes
        participant_ids: extractedParticipantIds
      };

      logger.debug('Donnees prestation a envoyer:', JSON.stringify(prestationData, null, 2));

      const prestationResponse = await fetch(prestationUrl, {
        method: prestationMethod,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(prestationData)
      });

      if (!prestationResponse.ok) {
        const errorText = await prestationResponse.text();
        throw new Error(`Erreur sauvegarde prestation: ${errorText}`);
      }

      const prestationResult = await prestationResponse.json();

      if (!prestationResult.success) {
        throw new Error(prestationResult.error || 'Erreur sauvegarde prestation');
      }

      if (!prestationId) {
        prestationId = prestationResult.data.id;
        logger.debug('Nouvelle prestation creee avec ID:', prestationId);
      }

      // Pour les blocages/notes sans participants
      if ((prestationType === 'blocked' || prestationType === 'note') && extractedParticipantIds.length === 0) {
        logger.debug(`=== TYPE ${prestationType.toUpperCase()} SANS PARTICIPANTS - SAUVEGARDE TERMINEE ===`);
        return { prestationId, daysSaved: 0, totalParticipantsSaved: 0 };
      }

      // ETAPE 2: GESTION DES JOURS ET PARTICIPANTS
      logger.debug('=== 2. GESTION DES JOURS ===');

      let savedDays = [];
      let totalParticipantsSaved = 0;

      if (isCreating) {
        // MODE CREATION
        for (const [index, day] of multiDayInfo.days.entries()) {
          const dayDate = day.date;
          const dayParticipantIds = dayParticipants[dayDate] || [];

          logger.debug(`Creation jour ${index + 1}/${multiDayInfo.days.length}: ${dayDate}`);

          // Creer le jour
          const dayResponse = await fetch(API_ENDPOINTS.PRESTATION_DAYS(prestationId), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ dates: [dayDate], copy_participants: false })
          });

          if (!dayResponse.ok) {
            throw new Error(`Erreur creation jour ${dayDate}`);
          }

          const dayResult = await dayResponse.json();
          if (!dayResult.success) {
            throw new Error(`Erreur creation jour ${dayDate}: ${dayResult.error}`);
          }

          const createdDay = dayResult.data[0];
          savedDays.push(createdDay);

          // Ajouter les participants
          for (const userId of dayParticipantIds) {
            const isOnSite = participantOnSite[dayDate]?.[userId] || false;

            const participantResponse = await fetch(API_ENDPOINTS.PRESTATION_DAYS_PARTICIPANTS(createdDay.id), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                action: 'add_participant',
                user_id: userId,
                is_on_site: isOnSite
              })
            });

            const participantResult = await participantResponse.json();
            if (participantResult.success) {
              totalParticipantsSaved++;
            }
          }
        }
      } else {
        // MODE MODIFICATION
        logger.debug('Mode modification - Mise a jour intelligente');

        // Recuperer l'etat actuel des jours
        const existingDaysResponse = await fetch(API_ENDPOINTS.PRESTATION_DAYS(prestationId), {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!existingDaysResponse.ok) {
          throw new Error('Erreur recuperation jours existants');
        }

        const existingDaysResult = await existingDaysResponse.json();
        const existingDays = existingDaysResult.success ? existingDaysResult.data : [];

        const existingDaysMap = {};
        existingDays.forEach(day => {
          existingDaysMap[day.day_date] = day;
        });

        // Traiter chaque jour
        for (const day of multiDayInfo.days) {
          const dayDate = day.date;
          const newParticipantIds = dayParticipants[dayDate] || [];

          let currentDay = existingDaysMap[dayDate];

          if (!currentDay) {
            // Creer le jour
            const dayResponse = await fetch(API_ENDPOINTS.PRESTATION_DAYS(prestationId), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ dates: [dayDate], copy_participants: false })
            });

            if (!dayResponse.ok) {
              throw new Error(`Erreur creation jour ${dayDate}`);
            }

            const dayResult = await dayResponse.json();
            if (!dayResult.success) {
              throw new Error(`Erreur creation jour ${dayDate}: ${dayResult.error}`);
            }

            currentDay = dayResult.data[0];
          }

          savedDays.push(currentDay);

          // Recuperer participants actuels
          const currentParticipantsResponse = await fetch(API_ENDPOINTS.PRESTATION_DAYS_PARTICIPANTS(currentDay.id), {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          const currentParticipantsResult = await currentParticipantsResponse.json();
          const currentParticipants = currentParticipantsResult.success ? currentParticipantsResult.data : [];
          const currentParticipantIds = currentParticipants.map(p => p.user_id);

          // Calculer les differences
          const toAdd = newParticipantIds.filter(id => !currentParticipantIds.includes(id));
          const toRemove = currentParticipantIds.filter(id => !newParticipantIds.includes(id));
          const toUpdate = newParticipantIds.filter(id => currentParticipantIds.includes(id));

          // Supprimer
          for (const userId of toRemove) {
            await fetch(API_ENDPOINTS.PRESTATION_DAYS_PARTICIPANTS(currentDay.id), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ action: 'remove_participant', user_id: userId })
            });
          }

          // Ajouter
          for (const userId of toAdd) {
            const isOnSite = participantOnSite[dayDate]?.[userId] || false;

            const addResponse = await fetch(API_ENDPOINTS.PRESTATION_DAYS_PARTICIPANTS(currentDay.id), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                action: 'add_participant',
                user_id: userId,
                is_on_site: isOnSite
              })
            });

            const addResult = await addResponse.json();
            if (addResult.success) {
              totalParticipantsSaved++;
            }
          }

          // Mettre a jour is_on_site pour les existants
          for (const userId of toUpdate) {
            const isOnSite = participantOnSite[dayDate]?.[userId] || false;
            const currentParticipant = currentParticipants.find(p => p.user_id === userId);

            // Mettre à jour seulement si is_on_site a changé
            if (currentParticipant && currentParticipant.is_on_site !== isOnSite) {
              await fetch(API_ENDPOINTS.PRESTATION_DAYS_PARTICIPANTS(currentDay.id), {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  action: 'add_participant',
                  user_id: userId,
                  is_on_site: isOnSite
                })
              });
            }
          }
        }
      }

      logger.debug('=== SAUVEGARDE TERMINEE ===');
      logger.debug(`Jours: ${savedDays.length}, Participants: ${totalParticipantsSaved}`);

      return { prestationId, daysSaved: savedDays.length, totalParticipantsSaved };

    } catch (error) {
      logger.error('Erreur sauvegarde:', error);
      throw error;
    }
  }, [editingPrestation?.id, formData, convertFormDateToUTC, token, multiDayInfo.days, cleanFormDataForServer]);

  // Fonction principale
  const handleSubmit = useCallback(async (sessionDays = [], dayParticipants = {}, participantOnSite = {}) => {
    logger.debug('=== DEBUT HANDLE SUBMIT ===');

    try {
      // Gestion des conflits
      if (conflicts.length > 0) {
        const proceed = await showConfirmation(
          `${conflicts.length} conflit(s) detecte(s). Voulez-vous continuer ?`,
          {
            title: 'Conflits detectes',
            details: conflicts.join('\n')
          }
        );

        if (!proceed) {
          return;
        }
      }

      // Sauvegarde
      const result = await saveMultiDaySession(sessionDays, dayParticipants, participantOnSite);

      // Tracking
      const isCreating = !editingPrestation?.id;
      trackAction(
        isCreating ? TRACKING_EVENTS.PRESTATION_CREATED : TRACKING_EVENTS.PRESTATION_UPDATED,
        'prestation',
        null,
        {
          prestationId: result?.prestationId,
          type: formData.prestation_type,
          groupId: formData.group_id,
          clientId: formData.client_id,
          daysCount: multiDayInfo?.days?.length || 1
        }
      );

      showSuccess('Prestation enregistree avec succes !');

      setTimeout(() => {
        onSave();
        onClose();
      }, 500);

      return result;

    } catch (err) {
      logger.error('Erreur dans handleSubmit:', err.message);
      showError('Erreur sauvegarde', { message: err.message });
      throw err;
    }
  }, [saveMultiDaySession, conflicts, showSuccess, showError, showConfirmation, onSave, onClose, editingPrestation?.id, trackAction, formData, multiDayInfo]);

  return {
    handleSubmit,
    saveMultiDaySession,
    convertFormDateToUTC,
    loading: false
  };
};

export default useSessionSaving;
